import type { SharedData } from '@/types';
import { route } from 'ziggy-js';
import type { Config, RouteParams, Router } from 'ziggy-js';

/**
 * The Ziggy configuration for the current page, built from the `ziggy`
 * shared prop, or null when the page carries none (a response rendered
 * outside the Inertia middleware, or a test fixture).
 *
 * That prop is the ONLY source of the route table: the Blade shell carries no
 * `@routes` script. It is shared once per session (see
 * `HandleInertiaRequests::share()`), and the browser and the SSR renderer both
 * read it from here so `route()` resolves identically on either side.
 */
export const ziggyConfigFrom = (props: Partial<Pick<SharedData, 'ziggy'>> | undefined): Config | null => {
    const ziggy = props?.ziggy;

    if (!ziggy || typeof ziggy.location !== 'string') {
        return null;
    }

    return { ...ziggy, location: new URL(ziggy.location) };
};

/**
 * Install the global `route()` helper that `<script setup>` blocks, composables
 * and module-scope config (`config/constants/navigation.ts` builds its hrefs
 * at import time) call directly. Templates get theirs from the ZiggyVue plugin.
 *
 * Ziggy would otherwise look for a global `Ziggy` object that only the removed
 * `@routes` directive used to define. Binding the config explicitly here means
 * the helper works in the browser and inside the Node SSR process alike.
 */
export const installGlobalRoute = (config: Config): void => {
    const bound = (name?: string, params?: RouteParams<string>, absolute?: boolean): string | Router =>
        name === undefined ? route(undefined, undefined, absolute, config) : route(name, params, absolute, config);

    (globalThis as unknown as { route: typeof bound }).route = bound;
};

/**
 * The page object Inertia embedded in the document for the first visit — the
 * same `<script data-page>` JSON the client adapter itself boots from.
 *
 * Read here as well, at module scope, so `route()` exists BEFORE Inertia
 * resolves the first page component: that resolution imports the page module
 * and everything it depends on, and some of those modules call `route()` while
 * they are being evaluated, long before the `setup()` callback runs. The old
 * `@routes` script had that timing for free by being inline in the head.
 */
export const readInitialPageProps = (id = 'app'): Partial<SharedData> | undefined => {
    if (typeof document === 'undefined') {
        return undefined;
    }

    const script =
        document.querySelector<HTMLScriptElement>(`script[data-page="${id}"]`) ?? document.querySelector<HTMLScriptElement>('script[data-page]');

    if (!script?.textContent) {
        return undefined;
    }

    try {
        return (JSON.parse(script.textContent) as { props?: Partial<SharedData> }).props;
    } catch {
        return undefined;
    }
};

/**
 * Browser boot: build the config from the embedded page and install `route()`.
 * Returns the config so the same object can be handed to the ZiggyVue plugin,
 * or null when the page carried no route table.
 *
 * The config's `location` is dropped here. The `ziggy` prop is shared once
 * per session, so its `location` is the URL of the FIRST page loaded — and
 * Ziggy's `current()` prefers a configured `location` over `window.location`.
 * Kept, it froze `route().current()` on that first page for the whole
 * session: after any Inertia navigation the Guide was told the person was
 * still on the page they started from. In the browser the live URL is the
 * truth; only the server render, which has no window, needs a fixed one.
 */
export const installGlobalRouteFromDocument = (): Config | null => {
    const config = ziggyConfigFrom(readInitialPageProps());

    if (!config) {
        return null;
    }

    const live: Config = { ...config, location: undefined };

    installGlobalRoute(live);

    return live;
};
